/**
 * Homepage rail — arrows + end states for the CSS scroll-snap card carousels.
 *
 * The rail itself scrolls natively (touch, trackpad, keyboard); this only wires the
 * desktop arrows and reflects the scroll position back into their disabled state.
 *
 * Scroll pitch is MEASURED on every click (first card width + the track's computed
 * column-gap, times how many whole cards fit) so one component serves every section
 * and every breakpoint without hardcoded widths.
 *
 * Markup contract (see snippets/homepage-card.liquid + the sections):
 *   [data-homepage-rail]        root
 *     [data-rail-track]         the scroller
 *     [data-rail-prev/next]     <button type="button">
 */

const REDUCED_MOTION = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * How far one arrow click travels.
 *
 * `step` comes from data-rail-step on the root: a card count ("1", "2", …) or
 * "page" for however many whole cards currently fit. Default is one card.
 * The card width + gap are measured, never hardcoded, so one component serves
 * every section and breakpoint.
 */
const railStep = (track, step) => {
  const first = track.firstElementChild;
  const cardWidth = first ? first.getBoundingClientRect().width : track.clientWidth;
  const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
  const pitch = cardWidth + gap;

  if (pitch <= 0) return track.clientWidth;

  if (step === 'page') {
    const perPage = Math.max(1, Math.floor((track.clientWidth + gap) / pitch));
    return pitch * perPage;
  }

  const cards = Math.max(1, parseInt(step, 10) || 1);
  return pitch * cards;
};

const setupRail = (root) => {
  const track = root.querySelector('[data-rail-track]');
  if (!track || !track.firstElementChild) return;

  const prev = root.querySelector('[data-rail-prev]');
  const next = root.querySelector('[data-rail-next]');

  let frame = null;

  const sync = () => {
    frame = null;

    const overflows = track.scrollWidth > track.clientWidth + 1;
    root.setAttribute('data-rail-overflow', overflows ? 'true' : 'false');

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;

    if (prev) {
      prev.disabled = !overflows || atStart;
      prev.classList.toggle('is-disabled', prev.disabled);
    }

    if (next) {
      next.disabled = !overflows || atEnd;
      next.classList.toggle('is-disabled', next.disabled);
    }
  };

  const queueSync = () => {
    if (frame !== null) return;
    frame = window.requestAnimationFrame(sync);
  };

  const advance = (direction) => {
    track.scrollBy({
      left: direction * railStep(track, root.dataset.railStep || '1'),
      behavior: REDUCED_MOTION() ? 'auto' : 'smooth',
    });
  };

  if (prev) prev.addEventListener('click', () => advance(-1));
  if (next) next.addEventListener('click', () => advance(1));

  track.addEventListener('scroll', queueSync, { passive: true });

  if ('ResizeObserver' in window) {
    new ResizeObserver(queueSync).observe(track);
  } else {
    window.addEventListener('resize', queueSync);
  }

  sync();
};

const initRails = (scope) => {
  const root = scope && scope.querySelectorAll ? scope : document;

  root.querySelectorAll('[data-homepage-rail]').forEach((rail) => {
    if (rail.dataset.railReady === 'true') return;
    rail.dataset.railReady = 'true';
    setupRail(rail);
  });
};

export default function initHomepageRail() {
  initRails(document);

  // Theme-editor re-renders never re-fire DOMContentLoaded
  document.addEventListener('shopify:section:load', (event) => {
    initRails(event.target || document);
  });
}
